import React, { useState, useEffect } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { AddressFields } from '@/components/AddressFields';
import { FormLabel, FieldError, RequiredNote } from '@/components/FormLabel';
import { focusFirstError, inputBorder, isBlank, type FieldErrors } from '@/lib/form-validation';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Package, Heart, UserCircle, Settings,
  Eye, EyeOff, Save, ShoppingBag, Trash2,
  Sun, Moon, Globe, DollarSign, RefreshCw,
  ChevronRight, MapPin, Calendar, Truck, ExternalLink,
  MessageSquare, Send,
} from 'lucide-react';
import { useUserStore } from '@/lib/user-store';
import { useAdminStore } from '@/lib/admin-store';
import { useAppStore } from '@/lib/store';
import { useTranslation, useCurrency } from '@/hooks/useTranslation';
import { fetchExchangeRate } from '@/lib/currency-rates';
import toast from 'react-hot-toast';
import type { OrderStatus, Address } from '@/types';
import { checkPassword } from '@/lib/password-policy';

const STATUS_COLORS: Record<OrderStatus, string> = {
  pending: 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400',
  confirmed: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
  processing: 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
  shipped: 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400',
  delivered: 'bg-green-500/10 text-green-600 dark:text-green-400',
  cancelled: 'bg-red-500/10 text-red-600 dark:text-red-400',
};

type Tab = 'orders' | 'messages' | 'wishlist' | 'account' | 'settings';

/** A stored address may predate address2 and state, so fill the gaps rather than leaving
 *  the inputs uncontrolled. */
function fillAddress(a?: Partial<Address> | null): Address {
  return {
    address: a?.address ?? '',
    address2: a?.address2 ?? '',
    city: a?.city ?? '',
    state: a?.state ?? '',
    country: a?.country ?? '',
    postalCode: a?.postalCode ?? '',
  };
}

/** Trims every address field. Listing them one by one is how address2 and state would get
 *  silently dropped on save the next time a field is added. */
function trimAddress(a: Address): Address {
  return {
    address: a.address.trim(),
    address2: (a.address2 ?? '').trim(),
    city: a.city.trim(),
    state: (a.state ?? '').trim(),
    country: a.country.trim(),
    postalCode: a.postalCode.trim(),
  };
}

const ProfilePage: React.FC = () => {
  const { t, localize } = useTranslation();
  const language = useAppStore((s) => s.language);
  const { formatPrice } = useCurrency();
  const currentUser = useUserStore((s) => s.currentUser);
  const updateProfile = useUserStore((s) => s.updateProfile);
  const changePassword = useUserStore((s) => s.changePassword);
  const toggleWishlist = useUserStore((s) => s.toggleWishlist);
  const storeOrders = useAdminStore((s) => s.orders);
  const artworks = useAdminStore((s) => s.artworks);
  const storeConversations = useAdminStore((s) => s.conversations);
  const addConversation = useAdminStore((s) => s.addConversation);
  const addMessageToConversation = useAdminStore((s) => s.addMessageToConversation);
  const markMessagesRead = useAdminStore((s) => s.markMessagesRead);

  const [myOrders, setMyOrders] = useState<typeof storeOrders>([]);
  const [myConversations, setMyConversations] = useState<typeof storeConversations>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as Tab | null;
  const [tab, setTab] = useState<Tab>(tabParam && ['orders', 'messages', 'wishlist', 'account', 'settings'].includes(tabParam) ? tabParam : 'orders');
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);
  const [expandedConv, setExpandedConv] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [showNewMsg, setShowNewMsg] = useState(false);
  const [newMsgSubject, setNewMsgSubject] = useState('');

  // Fetch user's orders from API
  useEffect(() => {
    if (!currentUser) return;
    import('@/lib/api').then(({ authApi }) => {
      authApi.getMyOrders().then((data) => {
        setMyOrders(data as typeof storeOrders);
      }).catch(() => {});
    });
  }, [currentUser]);

  // Fetch user's conversations from API
  useEffect(() => {
    if (!currentUser) return;
    import('@/lib/api').then(({ authApi }) => {
      authApi.getMyConversations().then((data) => {
        setMyConversations(data as typeof storeConversations);
      }).catch(() => {});
    });
  }, [currentUser]);

  // Merge API + store conversations, filter to profile source only (not contact/custom-frame)
  const conversations = React.useMemo(() => {
    const apiIds = new Set(myConversations.map((c) => c._id));
    const newFromStore = storeConversations.filter((c) => c.userId === currentUser?._id && !apiIds.has(c._id));
    return [...myConversations, ...newFromStore].filter((c) => c.source === 'profile' || !c.source);
  }, [myConversations, storeConversations, currentUser]);

  // Sync tab from URL search params
  React.useEffect(() => {
    const urlTab = searchParams.get('tab') as Tab | null;
    if (urlTab && ['orders', 'messages', 'wishlist', 'account', 'settings'].includes(urlTab)) {
      setTab(urlTab);
    }
  }, [searchParams]);

  // Account form
  const [accountForm, setAccountForm] = useState({
    firstName: currentUser?.firstName ?? '',
    lastName: currentUser?.lastName ?? '',
    email: currentUser?.email ?? '',
    phone: currentUser?.phone ?? '',
  });

  const savedShipping = currentUser?.shippingAddress ?? currentUser?.address;
  const [shippingAddr, setShippingAddr] = useState<Address>(() => fillAddress(savedShipping));
  const [billingAddr, setBillingAddr] = useState<Address>(() => fillAddress(currentUser?.billingAddress));

  // Password form
  const [pwForm, setPwForm] = useState({ oldPassword: '', newPassword: '', confirmPassword: '' });
  const [showPw, setShowPw] = useState(false);

  if (!currentUser) {
    return <Navigate to="/login" state={{ from: '/profile' }} replace />;
  }

  // Merge API orders + store orders (only recent pending from store, prefer API data)
  const orders = React.useMemo(() => {
    const apiIds = new Set(myOrders.map((o) => o._id));
    const thirtySecondsAgo = Date.now() - 30000;
    const newFromStore = storeOrders.filter((o) =>
      o.userId === currentUser._id &&
      !apiIds.has(o._id) &&
      new Date(o.createdAt).getTime() > thirtySecondsAgo
    );
    return [...myOrders, ...newFromStore];
  }, [myOrders, storeOrders, currentUser]);

  const userOrders = orders
    .filter((o) => o.userId === currentUser._id)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  const userConversations = conversations
    .filter((c) => c.userId === currentUser._id)
    .sort((a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime());

  const wishlistArtworks = artworks.filter((aw) => currentUser.wishlist.includes(aw._id));

  const tabs: Array<{ id: Tab; label: string; icon: React.ReactNode; count?: number }> = [
    { id: 'orders', label: t.profile.orders, icon: <Package size={15} />, count: userOrders.length },
    { id: 'messages', label: t.inbox.yourMessages, icon: <MessageSquare size={15} />, count: userConversations.length },
    { id: 'wishlist', label: t.profile.wishlist, icon: <Heart size={15} />, count: wishlistArtworks.length },
    { id: 'account', label: t.profile.account, icon: <UserCircle size={15} /> },
    { id: 'settings', label: t.profile.settings, icon: <Settings size={15} /> },
  ];

  // Nothing is flagged until Save is pressed; a field clears as soon as it is being fixed.
  const [accountErrors, setAccountErrors] = useState<FieldErrors>({});
  const clearAccountError = (field: string): void => {
    setAccountErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleSaveProfile = (): void => {
    const found: FieldErrors = {};
    if (isBlank(accountForm.firstName)) found.firstName = t.form.requiredField;
    if (isBlank(accountForm.lastName)) found.lastName = t.form.requiredField;
    setAccountErrors(found);
    if (Object.keys(found).length > 0) {
      toast.error(t.checkout.fixErrors);
      focusFirstError(['firstName', 'lastName'], found);
      return;
    }

    // email is deliberately not sent: it is the sign-in identity and the server ignores it,
    // so passing it through would only promise a change that never happens.
    updateProfile({
      firstName: accountForm.firstName.trim(),
      lastName: accountForm.lastName.trim(),
      phone: accountForm.phone.trim(),
      shippingAddress: trimAddress(shippingAddr),
      billingAddress: trimAddress(billingAddr),
      address: trimAddress(shippingAddr),
    });
    toast.success(t.profile.profileUpdated);
  };

  const handleChangePassword = (): void => {
    if (!checkPassword(pwForm.newPassword).ok) { toast.error(t.auth.passwordMinLength); return; }
    if (pwForm.newPassword !== pwForm.confirmPassword) { toast.error(t.auth.passwordsDontMatch); return; }
    const success = changePassword(pwForm.oldPassword, pwForm.newPassword);
    if (success) {
      toast.success(t.profile.passwordChanged);
      setPwForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
    } else {
      toast.error(t.profile.wrongPassword);
    }
  };

  const formatDate = (date: string): string =>
    new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const FIELD_BASE = "w-full px-4 py-3 sm:py-3.5 bg-black/[0.03] dark:bg-white/[0.05] border rounded-xl text-sm font-medium focus:outline-none focus:border-black/20 dark:focus:border-white/20 transition-all placeholder:text-black/20 dark:placeholder:text-white/20";
  const INPUT_CLS = inputBorder(FIELD_BASE, false);
  const LABEL_CLS = "block text-[9px] font-bold uppercase tracking-[0.2em] text-black/40 dark:text-white/40 mb-2";

  const memberSince = new Date(currentUser.createdAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <div className="min-h-screen bg-white dark:bg-black transition-colors duration-700">
      {/* ═══ Profile Header ═══ */}
      <div className="relative overflow-hidden">
        {/* Subtle background pattern */}
        <div className="absolute inset-0 opacity-[0.015] dark:opacity-[0.03]">
          <div className="absolute top-0 right-0 w-96 h-96 bg-black dark:bg-white rounded-full blur-[120px] translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-black dark:bg-white rounded-full blur-[100px] -translate-x-1/2 translate-y-1/2" />
        </div>

        <div className="relative max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 pt-28 sm:pt-32 pb-8 sm:pb-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col sm:flex-row items-center sm:items-end gap-5 sm:gap-6"
          >
            {/* Avatar */}
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-black dark:bg-white text-white dark:text-black flex items-center justify-center text-2xl sm:text-3xl font-syncopate font-bold shadow-lg">
              {currentUser.firstName.charAt(0).toUpperCase()}
            </div>

            {/* Info */}
            <div className="flex-1 text-center sm:text-left">
              <h1 className="text-2xl sm:text-3xl font-syncopate font-bold tracking-tighter">
                {currentUser.firstName} {currentUser.lastName}
              </h1>
              <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.25em] text-black/30 dark:text-white/30 mt-1">
                {currentUser.email}
              </p>
              <div className="flex items-center justify-center sm:justify-start gap-4 mt-2.5">
                {currentUser.address?.city && (
                  <span className="flex items-center gap-1 text-[9px] font-medium text-black/25 dark:text-white/25">
                    <MapPin size={10} /> {currentUser.address.city}
                  </span>
                )}
                <span className="flex items-center gap-1 text-[9px] font-medium text-black/25 dark:text-white/25">
                  <Calendar size={10} /> {memberSince}
                </span>
              </div>
            </div>

            {/* Stats */}
            <div className="flex items-center gap-6 sm:gap-8">
              <div className="text-center">
                <p className="text-xl sm:text-2xl font-syncopate font-bold">{userOrders.length}</p>
                <p className="text-[8px] sm:text-[9px] font-bold uppercase tracking-[0.2em] text-black/25 dark:text-white/25">{t.profile.orders}</p>
              </div>
              <div className="w-px h-8 bg-black/[0.06] dark:bg-white/[0.06]" />
              <div className="text-center">
                <p className="text-xl sm:text-2xl font-syncopate font-bold">{wishlistArtworks.length}</p>
                <p className="text-[8px] sm:text-[9px] font-bold uppercase tracking-[0.2em] text-black/25 dark:text-white/25">{t.profile.wishlist}</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom border */}
        <div className="h-px bg-black/[0.04] dark:bg-white/[0.04]" />
      </div>

      {/* ═══ Content Area ═══ */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        {/* Tab bar */}
        <div className="sticky top-16 sm:top-[72px] z-30 bg-white/80 dark:bg-black/80 backdrop-blur-xl -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex items-center gap-1.5 sm:gap-2 overflow-x-auto pb-0.5 scrollbar-hide">
            {tabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`relative flex items-center gap-2 px-4 sm:px-5 py-2.5 sm:py-3 rounded-xl text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.1em] whitespace-nowrap transition-all duration-300 ${
                  tab === t.id
                    ? 'bg-black dark:bg-white text-white dark:text-black shadow-sm'
                    : 'text-black/25 dark:text-white/25 hover:text-black/60 dark:hover:text-white/60 hover:bg-black/[0.03] dark:hover:bg-white/[0.03]'
                }`}
              >
                {t.icon}
                <span className="hidden xs:inline sm:inline">{t.label}</span>
                {t.count !== undefined && t.count > 0 && (
                  <span className={`text-[8px] tabular-nums ${tab === t.id ? 'opacity-50' : 'opacity-30'}`}>
                    {t.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Tab content */}
        <div className="pt-6 sm:pt-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={tab}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            >
              {/* ═══ ORDERS TAB ═══ */}
              {tab === 'orders' && (
                <div className="space-y-4">
                  {userOrders.length === 0 ? (
                    <div className="text-center py-20">
                      <div className="w-20 h-20 rounded-2xl bg-black/[0.03] dark:bg-white/[0.03] flex items-center justify-center mx-auto mb-5">
                        <ShoppingBag size={32} className="text-black/10 dark:text-white/10" />
                      </div>
                      <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-black/20 dark:text-white/20 mb-1">
                        {t.profile.noOrders}
                      </p>
                      <p className="text-[10px] text-black/15 dark:text-white/15 mb-5">
                        Start exploring our collection
                      </p>
                      <Link
                        to="/shop"
                        className="inline-flex items-center gap-2 px-6 py-3 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-[0.2em] rounded-xl hover:opacity-90 transition-all"
                      >
                        {t.artwork.backToShop} <ChevronRight size={12} />
                      </Link>
                    </div>
                  ) : (
                    userOrders.map((order) => {
                      const isExpanded = expandedOrderId === order._id;
                      return (
                      <motion.div
                        key={order._id}
                        layout
                        className="bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl overflow-hidden"
                      >
                        {/* Order header — clickable */}
                        <button
                          onClick={() => setExpandedOrderId(isExpanded ? null : order._id)}
                          className="w-full p-4 sm:p-5 space-y-3 text-left hover:bg-black/[0.01] dark:hover:bg-white/[0.01] transition-colors"
                        >
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                            <div className="flex items-center gap-3">
                              <span className="text-[10px] font-mono font-bold text-black/40 dark:text-white/40">
                                {order.orderNumber || `#${order._id.slice(0, 8)}`}
                              </span>
                              <span className={`px-2.5 py-1 rounded-full text-[9px] font-bold uppercase tracking-wider ${STATUS_COLORS[order.status]}`}>
                                {order.status}
                              </span>
                              <span className="text-[10px] text-black/30 dark:text-white/30">{order.items.length} item{order.items.length !== 1 ? 's' : ''}</span>
                            </div>
                            <div className="flex items-center gap-3 sm:text-right">
                              <p className="text-sm font-syncopate font-bold">{formatPrice(order.total, order.currency)}</p>
                              <p className="text-[9px] text-black/30 dark:text-white/30">{formatDate(order.createdAt)}</p>
                              <ChevronRight size={14} className={`text-black/20 dark:text-white/20 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                            </div>
                          </div>
                          {/* Thumbnail strip */}
                          <div className="flex -space-x-2">
                            {order.items.slice(0, 5).map((item, i) => (
                              <img key={i} src={item.artworkImage} alt="" className="w-10 h-10 rounded-lg object-cover border-2 border-white dark:border-black" />
                            ))}
                            {order.items.length > 5 && (
                              <div className="w-10 h-10 rounded-lg bg-black/5 dark:bg-white/5 border-2 border-white dark:border-black flex items-center justify-center text-[9px] font-bold">+{order.items.length - 5}</div>
                            )}
                          </div>
                        </button>

                        {/* Expanded detail */}
                        <AnimatePresence>
                          {isExpanded && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.25 }}
                              className="overflow-hidden"
                            >
                              <div className="px-4 sm:px-5 pb-5 pt-1 space-y-4 border-t border-black/[0.04] dark:border-white/[0.04]">
                                {/* Tracking */}
                                {order.trackingId && (
                                  <div className="flex items-center gap-2 p-3 rounded-xl bg-indigo-500/5 dark:bg-indigo-500/10 border border-indigo-500/10">
                                    <Truck size={14} className="text-indigo-500 shrink-0" />
                                    <div className="flex-1">
                                      <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-indigo-500/60 mb-0.5">Tracking Number</p>
                                      <p className="text-[11px] font-bold font-mono text-indigo-600 dark:text-indigo-400">{order.trackingId}</p>
                                    </div>
                                    {order.trackingUrl && (
                                      <a href={order.trackingUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 px-3 py-1.5 bg-indigo-500 text-white text-[9px] font-bold uppercase tracking-wider rounded-lg hover:bg-indigo-600 transition-colors">
                                        Track <ExternalLink size={10} />
                                      </a>
                                    )}
                                  </div>
                                )}

                                {/* Items detail */}
                                <div>
                                  <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-black/30 dark:text-white/30 mb-2">Items</p>
                                  <div className="space-y-2">
                                    {order.items.map((item, idx) => {
                                      // Orders store the artwork id; the artwork page is addressed by slug, so the
                                      // link only appears while the work still exists in the catalogue.
                                      const linkedArtwork = artworks.find((aw) => aw._id === item.artworkId);
                                      const rowCls = `flex items-center gap-3 p-3 rounded-xl bg-black/[0.02] dark:bg-white/[0.02]${linkedArtwork ? ' hover:bg-black/[0.05] dark:hover:bg-white/[0.06] transition-colors cursor-pointer' : ''}`;
                                      const row = (
                                      <>
                                        <img src={item.artworkImage} alt="" className="w-14 h-14 rounded-lg object-cover shrink-0" />
                                        <div className="flex-1 min-w-0">
                                          <p className="text-[12px] font-bold truncate">{item.artworkTitle?.en || item.artworkTitle?.ka || 'Artwork'}</p>
                                          <div className="flex items-center gap-2 mt-0.5">
                                            <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-black/[0.04] dark:bg-white/[0.06] text-black/40 dark:text-white/40">{item.frame}</span>
                                            <span className="text-[10px] text-black/40 dark:text-white/40">Qty: {item.quantity}</span>
                                          </div>
                                        </div>
                                        <div className="text-right shrink-0">
                                          <p className="text-[11px] font-bold">{formatPrice(item.totalPrice, order.currency)}</p>
                                          <p className="text-[9px] text-black/25 dark:text-white/25">{formatPrice(item.unitPrice, order.currency)} each</p>
                                        </div>
                                      </>
                                      );
                                      return linkedArtwork ? (
                                        <Link key={idx} to={`/artwork/${linkedArtwork.slug}`} className={rowCls}>{row}</Link>
                                      ) : (
                                        <div key={idx} className={rowCls}>{row}</div>
                                      );
                                    })}
                                  </div>
                                </div>

                                {/* Pricing breakdown */}
                                <div className="p-3 rounded-xl bg-black/[0.02] dark:bg-white/[0.02] space-y-2">
                                  <div className="flex justify-between text-[11px]">
                                    <span className="text-black/40 dark:text-white/40">Subtotal</span>
                                    <span className="font-medium">{formatPrice(order.subtotal, order.currency)}</span>
                                  </div>
                                  <div className="flex justify-between text-[11px]">
                                    <span className="text-black/40 dark:text-white/40">Shipping</span>
                                    <span className="font-medium">{order.shipping === 0 ? 'Free' : formatPrice(order.shipping, order.currency)}</span>
                                  </div>
                                  {order.tax > 0 && (
                                    <div className="flex justify-between text-[11px]">
                                      <span className="text-black/40 dark:text-white/40">Tax</span>
                                      <span className="font-medium">{formatPrice(order.tax, order.currency)}</span>
                                    </div>
                                  )}
                                  <div className="flex justify-between text-[12px] font-bold pt-2 border-t border-black/[0.06] dark:border-white/[0.06]">
                                    <span>Total</span>
                                    <span className="font-syncopate">{formatPrice(order.total, order.currency)}</span>
                                  </div>
                                </div>

                                {/* Shipping address */}
                                {order.shippingAddress && (
                                  <div className="flex items-start gap-2">
                                    <MapPin size={13} className="text-black/30 dark:text-white/30 mt-0.5 shrink-0" />
                                    <div>
                                      <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-black/30 dark:text-white/30 mb-0.5">Shipping Address</p>
                                      <p className="text-[11px] text-black/60 dark:text-white/60">
                                        {[order.shippingAddress.address, order.shippingAddress.city, order.shippingAddress.country, order.shippingAddress.postalCode].filter(Boolean).join(', ')}
                                      </p>
                                    </div>
                                  </div>
                                )}

                                {/* Order date & delivery method */}
                                <div className="flex flex-wrap gap-4">
                                  <div className="flex items-center gap-2">
                                    <Calendar size={13} className="text-black/30 dark:text-white/30" />
                                    <div>
                                      <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-black/30 dark:text-white/30">Ordered</p>
                                      <p className="text-[11px] font-medium">{formatDate(order.createdAt)}</p>
                                    </div>
                                  </div>
                                  {order.deliveryMethod && (
                                    <div className="flex items-center gap-2">
                                      <Truck size={13} className="text-black/30 dark:text-white/30" />
                                      <div>
                                        <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-black/30 dark:text-white/30">Delivery</p>
                                        <p className="text-[11px] font-medium capitalize">{order.deliveryMethod}</p>
                                      </div>
                                    </div>
                                  )}
                                  {order.paymentStatus && (
                                    <div className="flex items-center gap-2">
                                      <Package size={13} className="text-black/30 dark:text-white/30" />
                                      <div>
                                        <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-black/30 dark:text-white/30">Payment</p>
                                        <p className="text-[11px] font-medium capitalize">{order.paymentStatus}</p>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                      );
                    })
                  )}
                </div>
              )}

              {/* ═══ WISHLIST TAB ═══ */}
              {tab === 'wishlist' && (
                <div>
                  {wishlistArtworks.length === 0 ? (
                    <div className="text-center py-20">
                      <div className="w-20 h-20 rounded-2xl bg-black/[0.03] dark:bg-white/[0.03] flex items-center justify-center mx-auto mb-5">
                        <Heart size={32} className="text-black/10 dark:text-white/10" />
                      </div>
                      <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-black/20 dark:text-white/20 mb-1">
                        {t.profile.emptyWishlist}
                      </p>
                      <p className="text-[10px] text-black/15 dark:text-white/15 mb-5">
                        Save artworks you love
                      </p>
                      <Link
                        to="/shop"
                        className="inline-flex items-center gap-2 px-6 py-3 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-[0.2em] rounded-xl hover:opacity-90 transition-all"
                      >
                        {t.artwork.backToShop} <ChevronRight size={12} />
                      </Link>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                      {wishlistArtworks.map((aw) => (
                        <div key={aw._id} className="group relative">
                          <Link to={`/artwork/${aw.slug}`}>
                            <div className="aspect-square rounded-2xl overflow-hidden bg-black/5 dark:bg-white/5">
                              <img
                                src={aw.images?.[0] || ''}
                                alt={localize(aw.title)}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                              />
                            </div>
                            <div className="mt-2.5 px-0.5">
                              <p className="text-[11px] sm:text-xs font-bold truncate">{localize(aw.title)}</p>
                              <p className="text-[10px] text-black/40 dark:text-white/40 mt-0.5">{formatPrice(aw.price.amount)}</p>
                            </div>
                          </Link>
                          <button
                            onClick={() => toggleWishlist(aw._id)}
                            className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/80 dark:bg-black/80 backdrop-blur-sm flex items-center justify-center text-red-500 hover:scale-110 active:scale-95 transition-transform shadow-sm"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ═══ ACCOUNT TAB ═══ */}
              {tab === 'account' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
                  {/* Edit Profile */}
                  <div className="space-y-6">
                    {/* Personal Info */}
                    <div className="bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl p-5 sm:p-6 space-y-5">
                      <h2 className="text-[10px] font-bold uppercase tracking-[0.2em] text-black/40 dark:text-white/40">
                        {t.profile.editProfile}
                      </h2>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <FormLabel className={LABEL_CLS} required>{t.auth.firstName}</FormLabel>
                          <input data-field="firstName" type="text" value={accountForm.firstName} onChange={(e) => { setAccountForm((p) => ({ ...p, firstName: e.target.value })); clearAccountError('firstName'); }} className={inputBorder(FIELD_BASE, !!accountErrors.firstName)} />
                          <FieldError message={accountErrors.firstName} />
                        </div>
                        <div>
                          <FormLabel className={LABEL_CLS} required>{t.auth.lastName}</FormLabel>
                          <input data-field="lastName" type="text" value={accountForm.lastName} onChange={(e) => { setAccountForm((p) => ({ ...p, lastName: e.target.value })); clearAccountError('lastName'); }} className={inputBorder(FIELD_BASE, !!accountErrors.lastName)} />
                          <FieldError message={accountErrors.lastName} />
                        </div>
                      </div>
                      <div>
                        {/* Read-only on purpose: this address is the sign-in identity, and the
                            server does not accept a change to it. An editable box that silently
                            did nothing was worse than no box at all. */}
                        <label className={LABEL_CLS}>{t.auth.email}</label>
                        <input type="email" value={accountForm.email} readOnly disabled className={INPUT_CLS + ' opacity-60 cursor-not-allowed'} />
                        <p className="text-[9px] text-black/30 dark:text-white/30 mt-1.5">{t.form.emailLocked}</p>
                      </div>
                      <div>
                        <FormLabel className={LABEL_CLS} optional>{t.auth.phone}</FormLabel>
                        <input type="tel" value={accountForm.phone} onChange={(e) => setAccountForm((p) => ({ ...p, phone: e.target.value }))} className={INPUT_CLS} />
                      </div>
                      <RequiredNote />
                    </div>

                    {/* Shipping Address */}
                    <div className="bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl p-5 sm:p-6 space-y-5">
                      <div>
                        <h2 className="text-[10px] font-bold uppercase tracking-[0.2em] text-black/40 dark:text-white/40">
                          {t.profile.shippingAddress}
                          <span className="ml-1.5 normal-case tracking-normal font-medium text-black/25 dark:text-white/25">({t.form.optional})</span>
                        </h2>
                        <p className="text-[9px] text-black/30 dark:text-white/30 mt-1.5">{t.profile.addressOptionalHint}</p>
                      </div>
                      <AddressFields
                        value={shippingAddr}
                        onChange={setShippingAddr}
                        language={language}
                        inputClassName={INPUT_CLS}
                        labelClassName={LABEL_CLS}
                        labels={{
                          address: t.checkout.address,
                          address2: t.checkout.address2,
                          city: t.checkout.city,
                          state: t.checkout.state,
                          stateUs: t.checkout.stateUs,
                          country: t.checkout.country,
                          postalCode: t.checkout.postalCode,
                        }}
                      />
                    </div>

                    {/* Billing Address */}
                    <div className="bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl p-5 sm:p-6 space-y-5">
                      <div>
                        <h2 className="text-[10px] font-bold uppercase tracking-[0.2em] text-black/40 dark:text-white/40">
                          {t.profile.billingAddress}
                          <span className="ml-1.5 normal-case tracking-normal font-medium text-black/25 dark:text-white/25">({t.form.optional})</span>
                        </h2>
                        <p className="text-[9px] text-black/30 dark:text-white/30 mt-1.5">{t.profile.addressOptionalHint}</p>
                      </div>
                      <AddressFields
                        value={billingAddr}
                        onChange={setBillingAddr}
                        language={language}
                        inputClassName={INPUT_CLS}
                        labelClassName={LABEL_CLS}
                        labels={{
                          address: t.checkout.address,
                          address2: t.checkout.address2,
                          city: t.checkout.city,
                          state: t.checkout.state,
                          stateUs: t.checkout.stateUs,
                          country: t.checkout.country,
                          postalCode: t.checkout.postalCode,
                        }}
                      />
                    </div>

                    <button
                      onClick={handleSaveProfile}
                      className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3.5 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-[0.15em] rounded-xl hover:opacity-90 active:scale-[0.98] transition-all"
                    >
                      <Save size={14} /> {t.profile.saveChanges}
                    </button>
                  </div>

                  {/* Change Password */}
                  <div className="bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl p-5 sm:p-6 space-y-5 self-start">
                    <h2 className="text-[10px] font-bold uppercase tracking-[0.2em] text-black/40 dark:text-white/40">
                      {t.profile.changePassword}
                    </h2>
                    <div>
                      <FormLabel className={LABEL_CLS} required>{t.profile.oldPassword}</FormLabel>
                      <div className="relative">
                        <input
                          type={showPw ? 'text' : 'password'}
                          value={pwForm.oldPassword}
                          onChange={(e) => setPwForm((p) => ({ ...p, oldPassword: e.target.value }))}
                          className={INPUT_CLS + ' pr-12'}
                        />
                        <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-black/20 dark:text-white/20 hover:text-black/40 dark:hover:text-white/40 transition-colors">
                          {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <FormLabel className={LABEL_CLS} required>{t.profile.newPassword}</FormLabel>
                      <input type={showPw ? 'text' : 'password'} value={pwForm.newPassword} onChange={(e) => setPwForm((p) => ({ ...p, newPassword: e.target.value }))} className={INPUT_CLS} />
                    </div>
                    <div>
                      <FormLabel className={LABEL_CLS} required>{t.profile.confirmNewPassword}</FormLabel>
                      <input type={showPw ? 'text' : 'password'} value={pwForm.confirmPassword} onChange={(e) => setPwForm((p) => ({ ...p, confirmPassword: e.target.value }))} className={INPUT_CLS} />
                    </div>
                    <button
                      onClick={handleChangePassword}
                      className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3.5 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-[0.15em] rounded-xl hover:opacity-90 active:scale-[0.98] transition-all"
                    >
                      <Save size={14} /> {t.profile.changePassword}
                    </button>
                  </div>
                </div>
              )}

              {/* ═══ SETTINGS TAB ═══ */}
              {/* ═══ MESSAGES TAB ═══ */}
              {tab === 'messages' && (
                <div className="space-y-4">
                  {/* New Message Button */}
                  <div className="flex justify-end">
                    <button
                      onClick={() => setShowNewMsg(true)}
                      className="flex items-center gap-2 px-5 py-2.5 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-[0.15em] rounded-xl hover:opacity-90 transition-opacity"
                    >
                      <Send size={13} /> {t.nav.home === 'Home' ? 'New Message' : 'ახალი შეტყობინება'}
                    </button>
                  </div>

                  {/* New Message Subject Dialog */}
                  <AnimatePresence>
                    {showNewMsg && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="p-5 bg-white dark:bg-black border border-black/[0.06] dark:border-white/[0.06] rounded-2xl space-y-3">
                          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-black/40 dark:text-white/40">
                            {t.nav.home === 'Home' ? 'What would you like to discuss?' : 'რა თემაზე გსურთ მოგვწეროთ?'}
                          </p>
                          <input
                            type="text"
                            value={newMsgSubject}
                            onChange={(e) => setNewMsgSubject(e.target.value)}
                            placeholder={t.nav.home === 'Home' ? 'Enter subject...' : 'შეიყვანეთ თემა...'}
                            className="w-full px-4 py-3 bg-black/[0.03] dark:bg-white/[0.05] border border-black/[0.06] dark:border-white/[0.08] rounded-xl text-sm font-medium outline-none focus:border-black/20 dark:focus:border-white/20 transition-all placeholder:text-black/20 dark:placeholder:text-white/20"
                            autoFocus
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={() => { setShowNewMsg(false); setNewMsgSubject(''); }}
                              className="px-4 py-2.5 text-[10px] font-bold uppercase tracking-wider text-black/40 dark:text-white/40 hover:text-black dark:hover:text-white transition-colors"
                            >
                              {t.nav.home === 'Home' ? 'Cancel' : 'გაუქმება'}
                            </button>
                            <button
                              onClick={() => {
                                if (!newMsgSubject.trim()) { toast.error(t.nav.home === 'Home' ? 'Please enter a subject' : 'გთხოვთ შეიყვანოთ თემა'); return; }
                                const now = new Date().toISOString();
                                const conv = addConversation({
                                  userId: currentUser._id,
                                  guestName: `${currentUser.firstName} ${currentUser.lastName}`,
                                  guestEmail: currentUser.email,
                                  subject: newMsgSubject.trim(),
                                  source: 'profile',
                                  messages: [],
                                  status: 'open',
                                  lastMessageAt: now,
                                });
                                setShowNewMsg(false);
                                setNewMsgSubject('');
                                setExpandedConv(conv._id);
                                toast.success(t.nav.home === 'Home' ? 'Conversation started!' : 'მიმოწერა დაიწყო!');
                              }}
                              className="flex-1 py-2.5 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-[0.15em] rounded-xl hover:opacity-90 transition-opacity"
                            >
                              {t.nav.home === 'Home' ? 'Start Conversation' : 'მიმოწერის დაწყება'}
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {userConversations.length === 0 && !showNewMsg ? (
                    <div className="text-center py-16">
                      <div className="w-20 h-20 rounded-2xl bg-black/[0.03] dark:bg-white/[0.03] flex items-center justify-center mx-auto mb-5">
                        <MessageSquare size={32} className="text-black/10 dark:text-white/10" />
                      </div>
                      <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-black/20 dark:text-white/20 mb-1">
                        {t.inbox.noConversations}
                      </p>
                    </div>
                  ) : userConversations.length > 0 ? (
                    userConversations.map((conv) => {
                      const isExpanded = expandedConv === conv._id;
                      const lastMsg = conv.messages[conv.messages.length - 1];
                      const hasUnread = conv.messages.some((m) => !m.read && m.senderType === 'admin');
                      return (
                        <motion.div
                          key={conv._id}
                          layout
                          className="bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl overflow-hidden"
                        >
                          {/* Summary row */}
                          <button
                            onClick={() => {
                              const newId = isExpanded ? null : conv._id;
                              setExpandedConv(newId);
                              if (newId) markMessagesRead(newId, 'user');
                            }}
                            className="w-full flex items-center gap-4 p-4 sm:p-5 text-left hover:bg-black/[0.02] dark:hover:bg-white/[0.02] transition-colors"
                          >
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                              conv.status === 'open' ? 'bg-green-500/10 text-green-500' : 'bg-black/[0.04] dark:bg-white/[0.04] text-black/30 dark:text-white/30'
                            }`}>
                              <MessageSquare size={18} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-bold truncate">{conv.subject}</p>
                                {hasUnread && <span className="w-2 h-2 rounded-full bg-blue-500 shrink-0" />}
                              </div>
                              <p className="text-[10px] text-black/40 dark:text-white/40 truncate mt-0.5">
                                {lastMsg?.content.slice(0, 80)}{(lastMsg?.content.length ?? 0) > 80 ? '...' : ''}
                              </p>
                            </div>
                            <div className="text-right shrink-0">
                              <span className={`inline-block px-2 py-0.5 rounded-full text-[8px] font-bold uppercase tracking-wider ${
                                conv.status === 'open' ? 'bg-green-500/10 text-green-500' : 'bg-black/[0.06] dark:bg-white/[0.06] text-black/30 dark:text-white/30'
                              }`}>
                                {conv.status === 'open' ? t.inbox.open : t.inbox.resolved}
                              </span>
                              <p className="text-[9px] text-black/25 dark:text-white/25 mt-1">
                                {formatDate(conv.lastMessageAt)}
                              </p>
                            </div>
                            <ChevronRight size={16} className={`text-black/20 dark:text-white/20 transition-transform shrink-0 ${isExpanded ? 'rotate-90' : ''}`} />
                          </button>

                          {/* Expanded conversation thread */}
                          <AnimatePresence>
                            {isExpanded && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.3 }}
                                className="overflow-hidden"
                              >
                                <div className="border-t border-black/[0.04] dark:border-white/[0.04] p-4 sm:p-5 space-y-3 max-h-96 overflow-y-auto">
                                  {conv.messages.map((msg) => (
                                    <div
                                      key={msg._id}
                                      className={`flex ${msg.senderType === 'admin' ? 'justify-start' : 'justify-end'}`}
                                    >
                                      <div className={`max-w-[80%] p-3 rounded-2xl ${
                                        msg.senderType === 'admin'
                                          ? 'bg-black/[0.04] dark:bg-white/[0.06] rounded-tl-sm'
                                          : 'bg-black dark:bg-white text-white dark:text-black rounded-tr-sm'
                                      }`}>
                                        <p className={`text-[9px] font-bold uppercase tracking-wider mb-1 ${
                                          msg.senderType === 'admin' ? 'text-black/40 dark:text-white/40' : 'text-white/60 dark:text-black/60'
                                        }`}>
                                          {msg.senderName}
                                        </p>
                                        <p className="text-xs leading-relaxed">{msg.content}</p>
                                        <p className={`text-[8px] mt-1.5 ${
                                          msg.senderType === 'admin' ? 'text-black/25 dark:text-white/25' : 'text-white/40 dark:text-black/40'
                                        }`}>
                                          {new Date(msg.createdAt).toLocaleString()}
                                        </p>
                                      </div>
                                    </div>
                                  ))}
                                </div>

                                {/* Reply form */}
                                {conv.status === 'open' && (
                                  <div className="border-t border-black/[0.04] dark:border-white/[0.04] p-4 sm:p-5">
                                    <div className="flex gap-3">
                                      <textarea
                                        value={replyText}
                                        onChange={(e) => setReplyText(e.target.value)}
                                        placeholder={t.inbox.replyPlaceholder}
                                        rows={2}
                                        className="flex-1 px-4 py-3 rounded-xl border border-black/[0.08] dark:border-white/[0.08] bg-transparent text-sm placeholder:text-black/25 dark:placeholder:text-white/25 focus:outline-none focus:ring-2 focus:ring-black/10 dark:focus:ring-white/10 resize-none"
                                      />
                                      <button
                                        onClick={() => {
                                          if (!replyText.trim()) return;
                                          addMessageToConversation(conv._id, {
                                            conversationId: conv._id,
                                            senderType: 'user',
                                            senderName: `${currentUser.firstName} ${currentUser.lastName}`,
                                            senderEmail: currentUser.email,
                                            content: replyText.trim(),
                                            read: false,
                                          });
                                          setReplyText('');
                                          toast.success(t.inbox.messageSent);
                                        }}
                                        disabled={!replyText.trim()}
                                        className="self-end px-4 py-3 bg-black dark:bg-white text-white dark:text-black rounded-xl hover:bg-black/90 dark:hover:bg-white/90 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                                      >
                                        <Send size={16} />
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </motion.div>
                      );
                    })
                  ) : null}
                </div>
              )}

              {tab === 'settings' && (
                <SettingsPanel />
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

/* ─── Settings Panel ──────────────────────────────────────────────── */

const SettingsPanel: React.FC = () => {
  const { t } = useTranslation();
  const { exchangeRate } = useCurrency();
  const isDarkMode = useAppStore((s) => s.isDarkMode);
  const language = useAppStore((s) => s.language);
  const currency = useAppStore((s) => s.currency);
  const toggleTheme = useAppStore((s) => s.toggleTheme);
  const setLanguage = useAppStore((s) => s.setLanguage);
  const setCurrency = useAppStore((s) => s.setCurrency);
  const setExchangeRate = useAppStore((s) => s.setExchangeRate);
  const [refreshing, setRefreshing] = useState(false);

  const handleRefreshRate = (): void => {
    setRefreshing(true);
    localStorage.removeItem('gallery-nbg-rate');
    fetchExchangeRate().then((rate) => {
      setExchangeRate(rate);
      setRefreshing(false);
      toast.success(t.settings.rateUpdated);
    });
  };

  const OPTION_CLS = (active: boolean): string =>
    `flex-1 flex items-center justify-center gap-2 py-3 sm:py-3.5 rounded-xl text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.1em] transition-all duration-300 cursor-pointer ${
      active
        ? 'bg-black dark:bg-white text-white dark:text-black shadow-sm'
        : 'text-black/30 dark:text-white/30 hover:text-black/60 dark:hover:text-white/60 hover:bg-black/[0.04] dark:hover:bg-white/[0.04]'
    }`;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-6">
      {/* Currency */}
      <div className="bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl p-5 sm:p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-black/[0.04] dark:bg-white/[0.05] flex items-center justify-center">
              <DollarSign size={15} className="text-black/40 dark:text-white/40" />
            </div>
            <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.15em] text-black/50 dark:text-white/50">
              {t.settings.currency}
            </p>
          </div>
          <button
            onClick={handleRefreshRate}
            disabled={refreshing}
            className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-wider text-black/25 dark:text-white/25 hover:text-black/50 dark:hover:text-white/50 transition-colors disabled:opacity-30"
          >
            <RefreshCw size={11} className={refreshing ? 'animate-spin' : ''} />
            {t.settings.refreshRate}
          </button>
        </div>

        <div className="flex gap-2 bg-black/[0.02] dark:bg-white/[0.02] rounded-xl p-1">
          <button onClick={() => setCurrency('USD')} className={OPTION_CLS(currency === 'USD')}>
            $ USD
          </button>
          <button onClick={() => setCurrency('GEL')} className={OPTION_CLS(currency === 'GEL')}>
            ₾ GEL
          </button>
        </div>

        <p className="text-[9px] text-black/25 dark:text-white/25 leading-relaxed">
          {t.settings.nbgRate}: <span className="font-bold text-black/40 dark:text-white/40">1 USD = {exchangeRate.toFixed(4)} GEL</span>
          <br />
          <span className="opacity-60">{t.settings.nbgSource}</span>
        </p>
      </div>

      {/* Language */}
      <div className="bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl p-5 sm:p-6 space-y-4">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-black/[0.04] dark:bg-white/[0.05] flex items-center justify-center">
            <Globe size={15} className="text-black/40 dark:text-white/40" />
          </div>
          <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.15em] text-black/50 dark:text-white/50">
            {t.settings.language}
          </p>
        </div>

        <div className="flex gap-2 bg-black/[0.02] dark:bg-white/[0.02] rounded-xl p-1">
          <button onClick={() => setLanguage('en')} className={OPTION_CLS(language === 'en')}>
            English
          </button>
          <button onClick={() => setLanguage('ka')} className={OPTION_CLS(language === 'ka')}>
            ქართული
          </button>
        </div>
      </div>

      {/* Theme */}
      <div className="bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl p-5 sm:p-6 space-y-4 lg:col-span-2">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-black/[0.04] dark:bg-white/[0.05] flex items-center justify-center">
            {isDarkMode ? <Moon size={15} className="text-white/40" /> : <Sun size={15} className="text-black/40" />}
          </div>
          <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.15em] text-black/50 dark:text-white/50">
            {t.settings.theme}
          </p>
        </div>

        <div className="flex gap-2 bg-black/[0.02] dark:bg-white/[0.02] rounded-xl p-1 max-w-md">
          <button onClick={() => { if (isDarkMode) toggleTheme(); }} className={OPTION_CLS(!isDarkMode)}>
            <Sun size={14} /> {t.settings.lightMode}
          </button>
          <button onClick={() => { if (!isDarkMode) toggleTheme(); }} className={OPTION_CLS(isDarkMode)}>
            <Moon size={14} /> {t.settings.darkMode}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
